import { useEffect, useState } from "react";
import { Radar } from "lucide-react";
import { api, riskColor } from "../lib/api";
import { PageHeader, Panel, NeonButton } from "../components/common";
import { RiskOrb } from "../components/risk/RiskOrb";
import { Switch } from "../components/ui/switch";
import { Slider } from "../components/ui/slider";

const DEFAULT = { amount: 499, account_age_days: 3, prior_chargebacks: 0, velocity_24h: 2, avs_match: true, cvv_match: true,
  is_3ds: false, country: "US", ip_country: "NG", product_type: "digital", device_known: false };
const NUMS = [["amount", "Amount ($)", 5000, 10], ["account_age_days", "Account age (days)", 1000, 1], ["prior_chargebacks", "Prior chargebacks", 5, 1], ["velocity_24h", "Txns last 24h", 20, 1]];
const BOOLS = [["avs_match", "AVS match"], ["cvv_match", "CVV match"], ["is_3ds", "3-D Secure"], ["device_known", "Known device"]];
const sel = "bg-field border border-fieldline rounded-sm px-2 py-1.5 font-mono text-xs w-full";

const Form = ({ f, set }) => (
  <div className="space-y-5">
    {NUMS.map(([k, l, max, step]) => (
      <div key={k}>
        <div className="flex justify-between"><span className="label-xs">{l}</span><span className="font-mono text-xs tabular">{f[k]}</span></div>
        <Slider className="mt-2" value={[f[k]]} max={max} step={step} onValueChange={([v]) => set({ ...f, [k]: v })} data-testid={`slider-${k}`} />
      </div>
    ))}
    <div className="grid grid-cols-2 gap-3">
      {BOOLS.map(([k, l]) => (
        <label key={k} className="flex items-center justify-between border border-line px-3 py-2 rounded-sm text-xs">
          {l}<Switch checked={f[k]} onCheckedChange={(v) => set({ ...f, [k]: v })} data-testid={`switch-${k}`} />
        </label>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-3">
      {[["country", ["US", "GB", "CA", "DE", "NG", "BR", "IN"]], ["ip_country", ["US", "GB", "CA", "DE", "NG", "BR", "IN"]], ["product_type", ["digital", "subscription", "physical"]]].map(([k, opts]) => (
        <div key={k}><p className="label-xs mb-1">{k.replace("_", " ")}</p>
          <select className={sel} value={f[k]} onChange={(e) => set({ ...f, [k]: e.target.value })} data-testid={`select-${k}`}>
            {opts.map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      ))}
    </div>
  </div>
);

const Result = ({ r }) => (
  <div data-testid="risk-result">
    <div className="flex items-end justify-between">
      <div><p className="label-xs">Chargeback probability</p>
        <p className="font-mono text-6xl font-extrabold tabular mt-2" style={{ color: riskColor(r.risk_score) }} data-testid="risk-score-value">{r.risk_score}%</p></div>
      <span className="font-mono uppercase text-sm font-bold px-3 py-1 border rounded-sm" style={{ color: riskColor(r.risk_score), borderColor: riskColor(r.risk_score) }} data-testid="risk-decision">{r.decision.replace("_", " ")}</span>
    </div>
    <ul className="mt-5 space-y-1 text-sm text-[#CCC]">{r.recommended_actions.map((a) => <li key={a}>→ {a}</li>)}</ul>
    <p className="label-xs mt-6 mb-2">Feature contributions (log-odds)</p>
    <div className="space-y-1.5" data-testid="risk-contributions">
      {r.contributions.map((c) => (
        <div key={c.feature} className="flex items-center gap-3 font-mono text-[11px]">
          <span className="w-44 text-white/45 truncate">{c.label}</span>
          <div className="flex-1 h-1.5 bg-divider relative"><div className="absolute top-0 h-full" style={{ left: c.impact < 0 ? `${50 + c.impact * 15}%` : "50%", width: `${Math.min(Math.abs(c.impact) * 15, 50)}%`, background: c.impact > 0 ? "#F87171" : "#CCFF00" }} /></div>
          <span className="w-12 text-right tabular">{c.impact > 0 ? "+" : ""}{c.impact}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function RiskLab() {
  const [f, setF] = useState(DEFAULT);
  const [r, setR] = useState(null);
  const [model, setModel] = useState(null);
  const run = () => api.post("/risk/score", f).then((res) => setR(res.data));
  useEffect(() => { api.get("/risk/model").then((res) => setModel(res.data)); run(); }, []); // eslint-disable-line

  return (
    <div data-testid="risk-lab">
      <PageHeader eyebrow="// pre-payment prediction" title="Risk Lab">
        <NeonButton onClick={run} data-testid="run-risk-score-btn"><Radar className="w-3.5 h-3.5" /> Score transaction</NeonButton>
      </PageHeader>
      <div className="grid grid-cols-12 gap-6">
        <Panel title="Transaction signals" className="col-span-12 lg:col-span-4"><Form f={f} set={setF} /></Panel>
        <Panel title="Risk core" className="col-span-12 lg:col-span-4"><RiskOrb score={r?.risk_score || 0} />
          {model && <div className="grid grid-cols-3 gap-3 font-mono text-xs mt-4" data-testid="model-metrics">
            <div><p className="label-xs">AUC</p><p className="mt-1 text-brand">{model.metrics.auc}</p></div>
            <div><p className="label-xs">Accuracy</p><p className="mt-1">{model.metrics.accuracy}</p></div>
            <div><p className="label-xs">Train n</p><p className="mt-1">{model.metrics.training_samples}</p></div>
          </div>}
        </Panel>
        <Panel title="Model verdict" className="col-span-12 lg:col-span-4">{r ? <Result r={r} /> : <p className="text-white/35 font-mono text-sm">scoring…</p>}</Panel>
      </div>
    </div>
  );
}
